import React, { useState, useEffect, useContext, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FileImage, Lock, ZoomIn, ZoomOut } from "lucide-react";
import { CopyIcon, ExternalLinkIcon } from "../../components/icons";
import { GET, POST } from "../../service/CRUDService";
import EntityConstants from "../../configs/EntityConstants";
import SettingOption from "../../components/SettingOption/SettingOption";
import { CgAttachment } from "react-icons/cg";
import Toast from "../../common/Toast/Toast";
import convertToBase64 from "../../utils/ConvertToBase64";
import PeviewImage from "../../resources/icons/previewImageLogo.svg";
import CopyToClipBoard from "../../utils/CopyToClipBoard";
import correct from "../../resources/icons/correct.svg";
import qrCodeIcon from "../../resources/icons/qrcode.svg";
import LinkedInIcon from "../../resources/icons/linkedin-outline.svg";
import LinkedInLiveIcon from "../../resources/icons/linkedin.svg";
import SettingsIcon from "../../resources/icons/circular-settings.svg";
import NotificationIcon from "../../resources/icons/notification.svg";
import QrCodeIcon from "../../resources/icons/qr-code.svg";
import BackArrowIcon from "../../resources/icons/breadcrumbArrowGray.svg";
import MailIcon from "../../resources/icons/email-setting.svg";
import { BtnSave } from "../SettingTemplate/SettingButton";
import SettingEmailAddress from "./SettingEmailAddress";
import EmailTemplateEditor from "../EmailTemplateEditor/EmailTemplateEditor";
import legacyCertificate from "../../resources/icons/legacycertificate.svg";
import QRCodeStyling from "qr-code-styling";
import AuthContext from "../../store/auth-context";
import { useTranslation } from "react-i18next";
import LogoUpload from "../../components/settings/LogoUpload";
import BrandingPreviewCard from "../../components/settings/BrandingPreviewCard";
import CustomDomainSection from "../../components/settings/CustomDomainSection";
import {
  getColorForPicker,
  getDefaultBrandColours,
  getEffectiveBrandColours,
  isValidHexColor,
  normalizeHexColor,
} from "../../utils/brandColours";
import { pageRootTestIds } from "../../testing/pageRootTestIds";
import GlobeIcon from "../../resources/icons/Globe_grey.svg";
import WarningAmberIcon from "../../resources/icons/warning-amber.svg";

const SOCIAL_HOST_RULES = {
  org_linkedin_url: ["linkedin.com"],
  org_twitter_url: ["twitter.com", "x.com"],
  org_instagram_url: ["instagram.com"],
  org_facebook_url: ["facebook.com"],
  org_youtube_url: ["youtube.com", "youtu.be"],
  org_whatsapp_url: ["whatsapp.com", "wa.me"],
};

const SETTINGS_NAV_IDS = {
  "Organisation Settings": "settings-organisation-nav",
  "Public Page Settings": "settings-public-page-nav",
  "Organisation Details": "settings-organisation-details-nav",
  "Notification Settings": "settings-notification-nav",
  "Verification Settings": "settings-verification-settings-nav",
  "Email Settings": "settings-email-settings-nav",
  "Customize QR Code": "settings-customize-qr-code-nav",
};

const SETTINGS_SUBMENU_IDS = {
  "Email Template": "settings-email-template-nav",
  "Email Address": "settings-email-address-nav",
};

const isAllowedSocialUrl = (raw, allowedHosts) => {
  try {
    const u = new URL(raw);
    if (!["http:", "https:"].includes(u.protocol)) return false;
    const host = u.hostname.toLowerCase();
    return allowedHosts.some((h) => host === h || host.endsWith(`.${h}`));
  } catch {
    return false;
  }
};

const mergeOrgDetailsWithUpdated = (prev, updated) => {
  const updatedColours =
    updated?.org_domain_colours &&
    typeof updated.org_domain_colours === "object"
      ? updated.org_domain_colours
      : {};
  const mergedColours = {
    ...(prev?.org_domain_colours || {}),
    ...updatedColours,
  };

  return {
    ...prev,
    ...(updated || {}),
    preview_public_url:
      updated &&
      Object.prototype.hasOwnProperty.call(updated, "preview_public_url")
        ? (updated.preview_public_url ?? "")
        : (prev?.preview_public_url ?? ""),
    org_domain_colours: {
      primary_colour: normalizeHexColor(mergedColours.primary_colour || ""),
      secondary_colour: normalizeHexColor(mergedColours.secondary_colour || ""),
    },
  };
};

function SettingTemplate() {
  const { t } = useTranslation();

  const [orgDetails, setOrgDetails] = useState({
    organisation_name: "",
    org_website_url: "",
    org_domain: "",
    preview_public_url: "",
    org_domain_colours: {
      primary_colour: "",
      secondary_colour: "",
    },
    org_logo_hyperlink: "",
    organisation_favicon: null,
    org_logo: null,
    enable_whatsapp_notification: false,
    enable_email_notification: false,
    org_qrcode_logo: null,
    org_did_key: null,
    org_did_image: null,
    org_linkedin_code: null,
    org_linkedin_name: null,
    org_facebook_url: "",
    org_twitter_url: "",
    org_instagram_url: "",
    org_youtube_url: "",
    org_whatsapp_url: "",
    org_linkedin_url: "",
  });

  const [verificationSettings, setVerificationSettings] = useState({
    is_vc_admin_approval_required: false,
    is_legacy_certificate_enabled: false,
    is_legacy_admin_approval_required: false,
    is_payment_required_for_legacy: false,
    is_payment_required_for_vc: false,
    verification_cost: null,
    verification_cost_vc: null,
  });
  const [unsavedQrCodeLogo, setUnsavedQrCodeLogo] = useState(null);
  const [isFormDataChanged, setIsFormDataChanged] = useState(false);
  const [copied, setCopied] = useState(false);
  const [previewIframeKey, setPreviewIframeKey] = useState(0);
  const [previewZoom, setPreviewZoom] = useState(1);
  const [customDomain, setCustomDomain] = useState(null);
  const [selectedMenu, setSelectedMenu] = useState("Organisation Settings");
  const [initialOrgDetails, setInitialOrgDetails] = useState({});
  const [initialVerificationDetails, setInitialVerificationDetails] = useState(
    {},
  );
  const [isAccordionOpen, setIsAccordionOpen] = useState(false); // Manage accordion state
  const [selectedSubMenu, setSelectedSubMenu] = useState("Email Template");
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [isEmail, setIsEmail] = useState("");
  const [isEmailSent, setIsEmailSent] = useState(false);
  const [isShowWarning, setIsShowWarning] = useState(false);
  const [isEmptyEmail, setIsEmptyEmail] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const activeSection = queryParams.get("section");
  const qrCodeRef = useRef(null);
  const [errors, setErrors] = useState({
    verification_cost_vc: "",
    verification_cost: "",
  });
  const [fieldErrors, setFieldErrors] = useState({
    org_website_url: "",
    org_logo_hyperlink: "",
    primary_colour: "",
    secondary_colour: "",
    org_linkedin_url: "",
    org_twitter_url: "",
    org_instagram_url: "",
    org_facebook_url: "",
    org_youtube_url: "",
    org_whatsapp_url: "",
  });
  const authContext = useContext(AuthContext);
  const effectiveBrandColours = getEffectiveBrandColours(
    orgDetails.org_domain_colours,
  );

  const buildOrgSettingsPayload = () => {
    return {
      organisation_name: orgDetails.organisation_name,
      org_website_url: orgDetails.org_website_url,
      org_domain_colours: {
        primary_colour: effectiveBrandColours.primary_colour,
        secondary_colour: effectiveBrandColours.secondary_colour,
      },
      org_logo_hyperlink: orgDetails.org_logo_hyperlink,
      organisation_favicon: orgDetails.organisation_favicon
        ? orgDetails.organisation_favicon
        : null,
      org_logo: orgDetails.org_logo ? orgDetails.org_logo : null,
      org_facebook_url: orgDetails.org_facebook_url,
      org_twitter_url: orgDetails.org_twitter_url,
      org_instagram_url: orgDetails.org_instagram_url,
      org_youtube_url: orgDetails.org_youtube_url,
      org_whatsapp_url: orgDetails.org_whatsapp_url,
      org_linkedin_url: orgDetails.org_linkedin_url,
    };
  };

  const buildPublicPageSettingsPayload = () => {
    const payload = {};

    const currentColours = getEffectiveBrandColours(
      orgDetails.org_domain_colours,
    );
    const initialColours = getEffectiveBrandColours(
      initialOrgDetails?.org_domain_colours,
    );
    const currentPrimary = normalizeHexColor(currentColours.primary_colour);
    const currentSecondary = normalizeHexColor(currentColours.secondary_colour);
    const initialPrimary = normalizeHexColor(initialColours.primary_colour);
    const initialSecondary = normalizeHexColor(initialColours.secondary_colour);

    const coloursChanged =
      currentPrimary !== initialPrimary ||
      currentSecondary !== initialSecondary;

    if (coloursChanged) {
      payload.org_domain_colours = {
        primary_colour: currentPrimary,
        secondary_colour: currentSecondary,
      };
    }

    return payload;
  };

  const userDetails = async () => {
    try {
      const response = await GET(EntityConstants.CURRENT_USER);
      const data = await response.json();
      if (response.ok) {
        setTestEmail(data.email);
        authContext.setUserType(
          data.user_type || data.type?.toLowerCase() || null,
        );
      } else {
        Toast({
          type: "error",
          title: t("settings:error"),
          message: data.error,
        });
      }
    } catch (error) {
      Toast({
        type: "error",
        title: t("settings:error"),
        message: t("settings:error_sending_test_email"),
      });
    }
  };

  const getLogoForQrCode = () => {
    return unsavedQrCodeLogo || orgDetails.org_qrcode_logo || PeviewImage;
  };

  useEffect(() => {
    const logo = getLogoForQrCode();

    if (!qrCodeRef.current) {
      qrCodeRef.current = new QRCodeStyling({
        width: 235,
        height: 235,
        data: "app.credissuer.com",
        image: logo,
        dotsOptions: {
          color: "#000",
          type: "rounded",
        },
        backgroundOptions: {
          color: "#fff",
        },
        imageOptions: {
          crossOrigin: "anonymous",
          margin: 10,
          imageSize: 0.8,
        },
      });
    } else {
      qrCodeRef.current.update({
        image: logo,
      });
    }
    const qrCodeElement = document.getElementById("qr-code");
    if (qrCodeElement) {
      qrCodeElement.innerHTML = "";
      qrCodeRef.current.append(qrCodeElement);
    }
  }, [unsavedQrCodeLogo, orgDetails.org_qrcode_logo]);

  useEffect(() => {
    if (qrCodeRef.current) {
      const qrCodeElement = document.getElementById("qr-code");
      if (qrCodeElement) {
        qrCodeElement.innerHTML = "";
        qrCodeRef.current.append(qrCodeElement);
      }
    }
  }, [selectedMenu]);

  useEffect(() => {
    userDetails();
  }, []);

  const fetchOrgEmailDetails = async () => {
    try {
      const emailDetailsResponse = await GET(EntityConstants.GET_EMAIL_DETAILS);
      const data = await emailDetailsResponse.json();
      setIsEmail(data.email);
      setIsEmailValid(validateEmail(data.email));
      setIsEmptyEmail(false);
      switch (data.status) {
        case "pending":
          setIsVerified(false);
          setIsEmailSent(false);
          setIsShowWarning(false);
          break;
        case "failed":
          setIsVerified(false);
          setIsEmailSent(false);
          setIsShowWarning(true);
          break;
        case "verified":
          setIsVerified(true);
          setIsEmailSent(true);
          setIsShowWarning(false);
          break;
        default:
          setIsEmptyEmail(true);
      }
    } catch (error) {
      if (error.response.status === 404) {
        setIsEmptyEmail(true);
        setIsEmail("");
      } else {
        Toast({
          type: "error",
          title: t("settings:error"),
          message: error.error || t("settings:error_occurred"),
        });
      }
    }
  };

  const validateURL = (url) => {
    if (!url) return true;
    try {
      new URL(url);
      return true;
    } catch (error) {
      return false;
    }
  };

  const handleVerifyEmail = async () => {
    try {
      const response = await POST(EntityConstants.POST_EMAIL_DETAILS, {
        email: isEmail,
      });

      if (response.ok) {
        setIsEmailSent(true);
        const data = await response.json();
        Toast({
          type: "success",
          title: t("settings:success"),
          message: data.message,
        });
        setIsFormDataChanged(false);
        fetchOrgEmailDetails();
      } else {
        const errorData = await response.json();
        Toast({
          type: "error",
          title: t("settings:error"),
          message: errorData.message,
        });
      }
    } catch (error) {
      Toast({
        type: "error",
        title: t("settings:error"),
        message: t("settings:error_verifying_email"),
      });
    }
  };
  const validateEmail = (email) => {
    if (!email) return true;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  useEffect(() => {
    async function fetchOrgDetails() {
      try {
        const response = await GET(EntityConstants.GET_ORG_DETAILS);
        const data = await response.json();
        const {
          tenant_name,
          organisation_name,
          organisation_id,
          org_website_url,
          org_domain,
          preview_public_url,
          org_domain_colours,
          org_logo_hyperlink,
          organisation_favicon,
          org_logo,
          enable_whatsapp_notification,
          enable_email_notification,
          org_qrcode_logo,
          created_by_name,
          updated_by_name,
          org_did_key,
          org_did_image,
          org_linkedin_code,
          org_linkedin_name,
          org_facebook_url,
          org_twitter_url,
          org_instagram_url,
          org_youtube_url,
          org_whatsapp_url,
          org_linkedin_url,
        } = data;
        const defaults = getDefaultBrandColours();
        const normalizedPrimary = normalizeHexColor(
          org_domain_colours?.primary_colour || "",
        );
        const normalizedSecondary = normalizeHexColor(
          org_domain_colours?.secondary_colour || "",
        );

        setOrgDetails({
          tenant_name,
          organisation_name,
          organisation_id,
          org_website_url,
          org_domain,
          preview_public_url: preview_public_url || "",
          org_domain_colours: {
            primary_colour: normalizedPrimary || defaults.primary_colour,
            secondary_colour: normalizedSecondary || defaults.secondary_colour,
          },
          org_logo_hyperlink,
          organisation_favicon,
          org_logo,
          enable_whatsapp_notification,
          enable_email_notification,
          org_qrcode_logo,
          created_by_name,
          updated_by_name,
          org_did_key,
          org_did_image,
          org_linkedin_code,
          org_linkedin_name,
          org_facebook_url,
          org_twitter_url,
          org_instagram_url,
          org_youtube_url,
          org_whatsapp_url,
          org_linkedin_url,
        });
        setInitialOrgDetails(data);
      } catch (error) {
        console.error("Error fetching organisation details:", error);
      }
    }
    fetchOrgDetails();
  }, []);

  useEffect(() => {
    fetchOrgEmailDetails();
  }, []);

  const fetchVerificationSettings = async () => {
    try {
      const response = await GET(EntityConstants.POST_VERIFICATION_SETTINGS);
      const data = await response.json();
      const {
        is_vc_admin_approval_required,
        is_legacy_certificate_enabled,
        is_legacy_admin_approval_required,
        is_payment_required_for_legacy,
        is_payment_required_for_vc,
        verification_cost_vc,
        verification_cost,
      } = data;

      setVerificationSettings({
        is_vc_admin_approval_required,
        is_legacy_certificate_enabled,
        is_legacy_admin_approval_required,
        is_payment_required_for_legacy,
        is_payment_required_for_vc,
        verification_cost_vc: verification_cost_vc
          ? verification_cost_vc / 100
          : "",
        verification_cost: verification_cost ? verification_cost / 100 : "",
      });
      setInitialVerificationDetails(data);
    } catch (error) {
      console.error("Error fetching organisation details:", error);
    }
  };

  useEffect(() => {
    fetchVerificationSettings();
  }, []);

  const handleMenuChange = (menu) => {
    if (isFormDataChanged && menu !== selectedMenu) {
      const confirmLeave = window.confirm(t("settings:changes_not_saved"));
      if (confirmLeave) {
        setOrgDetails(initialOrgDetails);
        setUnsavedQrCodeLogo(null);
        setVerificationSettings(initialVerificationDetails);
        setIsFormDataChanged(false);
      } else {
        return;
      }
    }
    if (menu === "Verification Settings") {
      fetchVerificationSettings();
    }
    if (menu === "Email Settings") {
      setIsAccordionOpen((prev) => {
        if (!prev) {
          setSelectedSubMenu("Email Template");
        }
        return !prev;
      });
      setSelectedMenu(menu);
    } else {
      setIsAccordionOpen(false);
      setSelectedMenu(menu);
    }
  };

  const handleSubMenuChange = (subMenu) => {
    setSelectedSubMenu(subMenu);
  };

  useEffect(() => {
    if (
      selectedMenu === "Email Settings" &&
      selectedSubMenu === "Email Address"
    ) {
      fetchOrgEmailDetails();
    }
  }, [selectedMenu, selectedSubMenu]);

  useEffect(() => {
    if (activeSection === "email-address") {
      setSelectedMenu("Email Settings");
      setSelectedSubMenu("Email Address");
      setIsAccordionOpen(true);
      fetchOrgEmailDetails();
    }
  }, [activeSection]);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isFormDataChanged) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [isFormDataChanged]);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (isFormDataChanged) {
        event.preventDefault();
        event.returnValue = "";
      }
    };

    const handlePopState = (event) => {
      if (isFormDataChanged) {
        const message = t("settings:unsaved_changes_leave");
        if (!window.confirm(message)) {
          event.preventDefault();
          window.history.pushState(null, null, window.location.pathname);
        }
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("popstate", handlePopState);
    };
  }, [isFormDataChanged]);

  useEffect(() => {
    window.history.pushState(null, null, window.location.pathname);
  }, []);

  const handlePostRequest = async (payload) => {
    try {
      const response = await POST(EntityConstants.POST_ORG_DETAILS, payload);
      if (response.ok) {
        let updated = null;
        try {
          updated = await response.json();
        } catch (_) {
          // ignore parse errors
        }
        Toast({
          type: "success",
          title: t("settings:success"),
          message: t("settings:changes_saved_successfully"),
        });
        if (updated) {
          setOrgDetails((prev) => mergeOrgDetailsWithUpdated(prev, updated));
          setInitialOrgDetails((prev) =>
            mergeOrgDetailsWithUpdated(prev, updated),
          );
          setPreviewIframeKey((k) => k + 1);
          setPreviewZoom(1);
        }
        setIsFormDataChanged(false);
      } else {
        const errorData = await response.json();
        console.log("error>>>", errorData);
        if (errorData.message && Array.isArray(errorData.message)) {
          errorData.message.forEach((msg) => {
            Toast({
              type: "error",
              title: t("settings:error"),
              message: msg,
            });
          });
        } else if (errorData.message) {
          Toast({
            type: "error",
            title: t("settings:error"),
            message: errorData.message,
          });
        } else {
          Toast({
            type: "error",
            title: t("settings:error"),
            message: t("settings:error_processing_request"),
          });
        }
      }
    } catch (error) {
      console.error("Error in POST request:", error);
    }
  };

  const updateVerificationSettings = async (payload) => {
    try {
      const response = await POST(
        EntityConstants.POST_VERIFICATION_SETTINGS,
        payload,
      );
      const data = await response.json();
      if (response.ok) {
        const permissions = await authContext.syncUserPermissions();
        if (!permissions) {
          Toast({
            type: "warning",
            title: t("settings:error"),
            message: t("settings:permissions_refresh_failed"),
          });
          return;
        }
        Toast({
          type: "success",
          title: t("settings:success"),
          message: t("settings:changes_saved_successfully"),
        });
        setIsFormDataChanged(false);
      } else {
        Toast({
          type: "error",
          title: t("settings:error"),
          message: data.error || t("settings:error_processing_request"),
        });
      }
    } catch (error) {
      Toast({
        type: "error",
        title: t("settings:error"),
        message: t("settings:error_occurred"),
      });
    }
  };

  const handleInputChange = (e) => {
    setIsEmail(e.target.value);
    const { id, value } = e.target;
    if (id === "org_from_mail") {
      setIsEmailValid(validateEmail(value));
    }
    if (
      id === "org_logo_hyperlink" ||
      id === "org_website_url" ||
      [
        "org_linkedin_url",
        "org_twitter_url",
        "org_instagram_url",
        "org_facebook_url",
        "org_youtube_url",
        "org_whatsapp_url",
      ].includes(id)
    ) {
      if (value.trim()) {
        let isValidURL = validateURL(value);
        if (
          isValidURL &&
          id !== "org_logo_hyperlink" &&
          id !== "org_website_url"
        ) {
          isValidURL = isAllowedSocialUrl(value, SOCIAL_HOST_RULES[id]);
        }
        setFieldErrors((prev) => ({
          ...prev,
          [id]: isValidURL
            ? ""
            : t(
                id === "org_website_url"
                  ? "settings:valid_website_url"
                  : "settings:valid_url",
              ),
        }));
      } else {
        setFieldErrors((prev) => ({
          ...prev,
          [id]: "",
        }));
      }
    }

    setOrgDetails((prevDetails) => ({
      ...prevDetails,
      [id]: value,
    }));

    if (
      fieldErrors[id] &&
      id !== "org_logo_hyperlink" &&
      id !== "org_website_url" &&
      ![
        "org_linkedin_url",
        "org_twitter_url",
        "org_instagram_url",
        "org_facebook_url",
        "org_youtube_url",
        "org_whatsapp_url",
      ].includes(id)
    ) {
      setFieldErrors((prev) => ({
        ...prev,
        [id]: "",
      }));
    }

    setIsFormDataChanged(true);
  };

  const handleColourChange = (key, rawValue) => {
    const normalized = normalizeHexColor(rawValue);

    setOrgDetails((prev) => ({
      ...prev,
      org_domain_colours: {
        ...(prev.org_domain_colours || {}),
        [key]: normalized,
      },
    }));

    if (!normalized) {
      setFieldErrors((prev) => ({ ...prev, [key]: "" }));
    } else if (!isValidHexColor(normalized)) {
      setFieldErrors((prev) => ({
        ...prev,
        [key]: t("settings:invalid_colour_code"),
      }));
    } else {
      setFieldErrors((prev) => ({ ...prev, [key]: "" }));
    }

    setIsFormDataChanged(true);
  };

  // Handle file upload for organisation logo
  const handleLogoUpload = async (file) => {
    const base64FileString = await convertToBase64(file);
    setOrgDetails((prevDetails) => ({
      ...prevDetails,
      org_logo: base64FileString,
    }));
    setIsFormDataChanged(true);
  };

  const handleFaviconUpload = async (file) => {
    const base64FileString = await convertToBase64(file);
    setOrgDetails((prevDetails) => ({
      ...prevDetails,
      organisation_favicon: base64FileString,
    }));
    setIsFormDataChanged(true);
  };

  const handleQrCodeUpload = async (file) => {
    const base64FileString = await convertToBase64(file);
    setUnsavedQrCodeLogo(base64FileString);
    setOrgDetails((prevDetails) => ({
      ...prevDetails,
      org_qrcode_logo: base64FileString,
    }));
    setIsFormDataChanged(true);
  };

  // Handle toggle changes and immediately update the backend
  const handleToggleChange = async (toggleField) => {
    const newValue = !orgDetails[toggleField];
    setOrgDetails((prevDetails) => ({
      ...prevDetails,
      [toggleField]: newValue,
    }));
    setIsFormDataChanged(true);
  };

  const handleVerificationToggleChange = (toggleField) => {
    const newValue = !verificationSettings[toggleField];
    setVerificationSettings((prevState) => {
      const updatedSettings = {
        ...prevState,
        [toggleField]: newValue,
      };

      // If the toggle is turned off, remove the corresponding verification cost
      if (!newValue) {
        if (toggleField === "is_payment_required_for_vc") {
          updatedSettings.verification_cost_vc = "";
        }
        if (toggleField === "is_payment_required_for_legacy") {
          updatedSettings.verification_cost = "";
        }
      }

      // Remove values from local storage if toggle is off
      if (!newValue) {
        if (toggleField === "is_payment_required_for_vc") {
          localStorage.removeItem("verCost");
        }
        if (toggleField === "is_payment_required_for_legacy") {
          localStorage.removeItem("verCostLegacy");
        }
      }

      return updatedSettings;
    });
    setIsFormDataChanged(true); // Mark the form as changed
  };

  const handleTestEmail = async () => {
    try {
      const response = await POST(EntityConstants.POST_TEST_EMAIL, {
        to_email: testEmail,
      });
      if (response.ok) {
        const data = await response.json();
        Toast({
          type: "success",
          title: t("settings:success"),
          message: data.message,
        });
        setIsFormDataChanged(false);
      } else {
        const errorData = await response.json();
        if (errorData.message && Array.isArray(errorData.message)) {
          errorData.message.forEach((msg) => {
            Toast({
              type: "error",
              title: t("settings:error"),
              message: msg,
            });
          });
        } else {
          Toast({
            type: "error",
            title: t("settings:error"),
            message: t("settings:error_processing_request"),
          });
        }
      }
    } catch (error) {
      console.error("Error in POST request:", error);
    }
  };

  const handleVerificationCostChange = (event) => {
    const { name, value } = event.target;

    // Handle rupees value and conversion to paisa
    const rupees = value.trim(); // Keep rupees as a string
    const paisa = Math.round(parseFloat(rupees || 0) * 100); // Convert rupees to paisa

    // Store paisa in localStorage separately for VC and Legacy
    if (name === "verification_cost_vc") {
      localStorage.setItem("verCost", paisa);
    } else if (name === "verification_cost") {
      localStorage.setItem("verCostLegacy", paisa);
    }

    // Check if the value is a valid number and update the state accordingly
    if (!isNaN(rupees) && rupees !== "") {
      const floatValue = parseFloat(rupees);
      setVerificationSettings((prevState) => ({
        ...prevState,
        [name]: floatValue,
      }));
    } else {
      setVerificationSettings((prevState) => ({
        ...prevState,
        [name]: "",
      }));
    }

    // Clear error when user starts typing
    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: value.trim() ? "" : t("settings:amount_required"),
    }));

    setIsFormDataChanged(true); // Mark the form as changed
  };

  const handleSave = async () => {
    if (
      selectedMenu === "Organisation Settings" ||
      selectedMenu === "Public Page Settings"
    ) {
      const errors = {};

      if (selectedMenu === "Organisation Settings") {
        if (!orgDetails.org_logo_hyperlink?.trim()) {
          errors.org_logo_hyperlink = t("settings:logo_hyperlink_required");
        } else if (!validateURL(orgDetails.org_logo_hyperlink)) {
          errors.org_logo_hyperlink = t("settings:valid_url");
        }

        Object.entries(SOCIAL_HOST_RULES).forEach(([field, allowedHosts]) => {
          const url = orgDetails[field]?.trim();
          if (url) {
            const isValid =
              validateURL(url) && isAllowedSocialUrl(url, allowedHosts);
            if (!isValid) {
              errors[field] = t("settings:valid_url");
            }
          }
        });
      }

      if (
        orgDetails.org_website_url?.trim() &&
        !validateURL(orgDetails.org_website_url)
      ) {
        errors.org_website_url = t("settings:valid_website_url");
      }

      const primary = orgDetails.org_domain_colours?.primary_colour;
      const secondary = orgDetails.org_domain_colours?.secondary_colour;

      if (primary?.trim() && !isValidHexColor(primary)) {
        errors.primary_colour = t("settings:invalid_colour_code");
      }
      if (secondary?.trim() && !isValidHexColor(secondary)) {
        errors.secondary_colour = t("settings:invalid_colour_code");
      }
      if (
        (primary?.trim() || secondary?.trim()) &&
        (!primary?.trim() || !secondary?.trim())
      ) {
        if (!primary?.trim()) {
          errors.primary_colour = t("settings:primary_colour_required");
        }
        if (!secondary?.trim()) {
          errors.secondary_colour = t("settings:secondary_colour_required");
        }
      }

      if (Object.keys(errors).length > 0) {
        setFieldErrors(errors);
        const errorMessages = Object.values(errors).join(" and ");
        Toast({
          type: "error",
          title: t("settings:error"),
          message: errorMessages,
        });
        return;
      }

      setFieldErrors({
        org_website_url: "",
        org_logo_hyperlink: "",
        primary_colour: "",
        secondary_colour: "",
        org_linkedin_url: "",
        org_twitter_url: "",
        org_instagram_url: "",
        org_facebook_url: "",
        org_youtube_url: "",
        org_whatsapp_url: "",
      });
    }

    let payload = {};
    switch (selectedMenu) {
      case "Organisation Settings": {
        payload = buildOrgSettingsPayload();
        break;
      }
      case "Public Page Settings": {
        payload = buildPublicPageSettingsPayload();
        break;
      }

      case "Organisation Details":
        payload = {
          org_linkedin_name: orgDetails.org_linkedin_name,
          org_linkedin_code: orgDetails.org_linkedin_code,
        };
        break;

      case "Notification Settings":
        payload = {
          enable_whatsapp_notification: orgDetails.enable_whatsapp_notification,
          enable_email_notification: orgDetails.enable_email_notification,
        };
        break;

      case "Customize QR Code":
        payload = {
          org_qrcode_logo: orgDetails.org_qrcode_logo
            ? orgDetails.org_qrcode_logo
            : null,
        };
        break;

      default:
        console.error("Unrecognized section:", { selectedMenu });
        return;
    }

    handlePostRequest(payload);
  };

  const handleVerificationSave = async () => {
    const newErrors = {};

    if (
      verificationSettings.is_payment_required_for_vc &&
      !verificationSettings.verification_cost_vc
    ) {
      newErrors.verification_cost_vc = t("settings:amount_required");
    }

    if (
      verificationSettings.is_payment_required_for_legacy &&
      !verificationSettings.verification_cost
    ) {
      newErrors.verification_cost = t("settings:amount_required");
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    let payload = {};
    switch (selectedMenu) {
      case "Verification Settings":
        payload = {
          is_vc_admin_approval_required:
            verificationSettings.is_vc_admin_approval_required
              ? verificationSettings.is_vc_admin_approval_required
              : false,
          is_payment_required_for_vc:
            verificationSettings.is_payment_required_for_vc
              ? verificationSettings.is_payment_required_for_vc
              : false,
          is_legacy_certificate_enabled:
            verificationSettings.is_legacy_certificate_enabled
              ? verificationSettings.is_legacy_certificate_enabled
              : false,
          is_legacy_admin_approval_required:
            verificationSettings.is_legacy_admin_approval_required
              ? verificationSettings.is_legacy_admin_approval_required
              : false,
          is_payment_required_for_legacy:
            verificationSettings.is_payment_required_for_legacy
              ? verificationSettings.is_payment_required_for_legacy
              : false,
          verification_cost:
            (verificationSettings.verification_cost || 0) * 100,
          verification_cost_vc:
            (verificationSettings.verification_cost_vc || 0) * 100,
        };
        break;

      default:
        console.error("Unrecognized section:", { selectedMenu });
        return;
    }

    await updateVerificationSettings(payload);
  };

  const handleCopyClick = (event, id) => {
    event.stopPropagation();
    CopyToClipBoard(id);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const handleEmailChange = (e) => {
    const updatedEmail = e.target.value;
    setIsEmail(updatedEmail);
    setIsEmailValid(updatedEmail ? validateEmail(updatedEmail) : true);
    setIsFormDataChanged(true);
    setIsEmailSent(false);
  };

  const Menus = [
    {
      title: t("settings:organisation_settings"),
      key: "Organisation Settings",
      src: SettingsIcon,
    },
    {
      title: t("settings:public_page_settings"),
      key: "Public Page Settings",
      src: GlobeIcon,
    },
    {
      title: t("settings:organisation_details"),
      key: "Organisation Details",
      src: LinkedInIcon,
    },
    {
      title: t("settings:notification_settings"),
      key: "Notification Settings",
      src: NotificationIcon,
    },
    {
      title: t("settings:customize_qr_code"),
      key: "Customize QR Code",
      src: QrCodeIcon,
    },
    {
      title: t("settings:verification_settings"),
      key: "Verification Settings",
      src: legacyCertificate,
    },
    {
      title: t("settings:email_settings"),
      key: "Email Settings",
      src: MailIcon,
    },
  ];

  return (
    <div
      className="min-h-screen bg-white flex flex-col pt-[6rem] w-full"
      id={pageRootTestIds.settingTemplate}
    >
      <div className="ml-[1.125rem]">
        <header className="text-xl font-semibold text-gray-900">
          {t("settings:settings")}
        </header>
        <section className="text-base leading-8 text-gray-900">
          {t("settings:admin_controls_enabled")}
        </section>
      </div>
      <div className="flex flex-col lg:flex-row">
        <div
          className={`${
            selectedMenu ? "w-[95%] lg:w-[18%]" : "w-20"
          } bg-dark-purple h-full xl:h-screen ml-[1.125rem] relative transition-all duration-300`}
        >
          <ul>
            {Menus.map((Menu, index) => (
              <button
                key={index}
                id={SETTINGS_NAV_IDS[Menu.key]}
                onClick={() => handleMenuChange(Menu.key)}
                className={`flex rounded-md p-2 cursor-pointer hover:bg-light-white text-gray-300 text-sm items-center gap-x-4 mt-2 hover:bg-[#F4F6F7] transition duration-500 ease-in-out w-full text-left  focus:outline-none ${
                  selectedMenu === Menu.key && "bg-[#F4F6F7]"
                }`}
              >
                <div
                  className={`bg-cfPrimaryLight rounded-3xl transition-all duration-50 ease-in-out ${
                    selectedMenu === Menu.key ? "w-1 h-7" : "w-0"
                  }`}
                ></div>
                <div className="w-[10%]">
                  <img src={`${Menu.src}`} height={60} width={60} alt="" />
                </div>
                <span
                  className={`${
                    selectedMenu
                      ? "text-gray-700 pt-1 flex w-[90%] justify-between font-normal origin-left duration-200"
                      : ""
                  }`}
                >
                  <div>{Menu.title}</div>
                  <div>
                    {Menu.key === "Email Settings" ? (
                      <img
                        src={BackArrowIcon}
                        alt="Toggle"
                        className={`transition-transform ${
                          isAccordionOpen ? "rotate-90" : ""
                        }`}
                      />
                    ) : (
                      <img src={BackArrowIcon} alt="" />
                    )}
                  </div>
                </span>
              </button>
            ))}
            {selectedMenu === "Email Settings" && (
              <div>
                <div
                  className={`accordion-content ${
                    isAccordionOpen ? "open" : ""
                  }`}
                >
                  <ul className="ml-[3.2rem] mt-1">
                    {[
                      {
                        title: t("settings:email_template"),
                        key: "Email Template",
                      },
                      {
                        title: t("settings:email_address"),
                        key: "Email Address",
                      },
                    ].map((subMenu, subIndex) => (
                      <button
                        key={subIndex}
                        id={SETTINGS_SUBMENU_IDS[subMenu.key]}
                        onClick={() => handleSubMenuChange(subMenu.key)}
                        className={`py-2 mb-1 font-normal text-sm text-gray-700 cursor-pointer hover:bg-[#F4F6F7] transition duration-500 ease-in-out rounded-md w-full text-left focus:outline-none ${
                          selectedSubMenu === subMenu.key ? "bg-[#F4F6F7]" : ""
                        }`}
                      >
                        <div className="flex items-center px-2 gap-x-4 justify-between">
                          <div
                            className={`bg-cfPrimaryLight rounded-3xl transition-all duration-50 ease-in-out ${
                              selectedSubMenu === subMenu.key
                                ? "w-1 h-6"
                                : "w-0"
                            }`}
                          ></div>
                          <span className="w-full">{subMenu.title}</span>
                          <img src={BackArrowIcon} alt="" />
                        </div>
                      </button>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </ul>
        </div>
        <div className="h-full flex-1">
          {selectedMenu === "Email Settings" && (
            <div>
              {selectedSubMenu === "Email Template" && (
                <EmailTemplateEditor
                  onCloneTemplateClick={(selectedTemplateId) =>
                    navigate(
                      `/issuer/clone-email-template?from=${encodeURIComponent(`${location.pathname}${location.search || ""}`)}&templateId=${encodeURIComponent(selectedTemplateId || "")}`,
                      {
                        state: {
                          returnState: {
                            ...(location.state || {}),
                          },
                        },
                      },
                    )
                  }
                />
              )}
              {selectedSubMenu === "Email Address" && (
                <SettingEmailAddress
                  isEmail={isEmail}
                  handleEmailChange={handleEmailChange}
                  isEmailValid={isEmailValid}
                  isEmailSent={isEmailSent}
                  isVerified={isVerified}
                  handleTestEmail={handleTestEmail}
                  handleVerifyEmail={handleVerifyEmail}
                  isShowWarning={isShowWarning}
                  fetchOrgEmailDetails={fetchOrgEmailDetails}
                  isEmptyEmail={isEmptyEmail}
                />
              )}
            </div>
          )}
          {selectedMenu === "Organisation Settings" && (
            <div className="flex flex-col">
              <div className="flex flex-col xl:flex-row w-full">
                <div className="p-3 xl:w-[50%]">
                  <div className="xs:pl-8 xs:pr-3 rounded-xl">
                    <header className="text-xl font-semibold mb-3 text-gray-900">
                      {t("settings:organisation_settings")}
                    </header>
                    <div className="mb-4">
                      <label
                        className="block mb-2 text-sm font-medium text-gray-900"
                        htmlFor="organisation_name"
                      >
                        {t("settings:organisation_name")}
                      </label>
                      <input
                        type="text"
                        id="organisation_name"
                        value={orgDetails.organisation_name}
                        onChange={handleInputChange}
                        className="bg-gray-50 border border-gray-300 text-gray-600 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                        placeholder="e.g. Credissuer"
                      />
                    </div>
                    <div className="mb-4">
                      <label
                        className="block mb-2 text-sm font-medium text-gray-900"
                        htmlFor="org_website_url"
                      >
                        {t("settings:organisation_website")}
                      </label>
                      <input
                        type="text"
                        id="org_website_url"
                        value={orgDetails.org_website_url}
                        onChange={handleInputChange}
                        className={`bg-gray-50 border text-gray-600 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 ${
                          fieldErrors.org_website_url
                            ? "border-red-500"
                            : "border-gray-300"
                        }`}
                        placeholder="e.g. https://www.credissuer.com"
                      />
                      {fieldErrors.org_website_url && (
                        <p className="text-red-500 text-sm mt-1">
                          {fieldErrors.org_website_url}
                        </p>
                      )}
                    </div>
                    <div className="mb-4">
                      <label
                        className="block mb-2 text-sm font-medium text-gray-900 after:content-['*'] after:text-red-600"
                        htmlFor="org_logo_hyperlink"
                      >
                        {t("settings:logo_hyperlink")}
                      </label>
                      <input
                        type="text"
                        id="org_logo_hyperlink"
                        value={orgDetails.org_logo_hyperlink}
                        onChange={handleInputChange}
                        className={`bg-gray-50 border text-gray-600 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 ${
                          fieldErrors.org_logo_hyperlink
                            ? "border-red-500"
                            : "border-gray-300"
                        }`}
                        placeholder="e.g. https://www.credflow.com/templates/logo/"
                      />
                      {fieldErrors.org_logo_hyperlink && (
                        <p className="text-red-500 text-sm mt-1">
                          {fieldErrors.org_logo_hyperlink}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="w-full rounded-xl mb-4">
                    <label
                      className="block text-sm font-medium text-gray-900"
                      htmlFor="org-favicon-upload"
                    >
                      {t("settings:organisation_favicon")}
                    </label>
                    <div className="gap-5 flex flex-col 2xl:flex-row">
                      <div className="items-center flex-initial w-full mt-[0rem] 2xl:mt-[1rem]">
                        <LogoUpload
                          id="org-favicon-upload"
                          logoHeading={t("settings:upload_your")}
                          logoSubHeading={t("settings:organisation_favicon")}
                          altText={t("settings:upload_favicon_image")}
                          onFileUpload={handleFaviconUpload}
                          acceptedExtensions={["png", "jpg", "jpeg"]}
                          acceptedMimeTypes={[
                            "image/png",
                            "image/jpeg",
                            "image/jpg",
                          ]}
                          imageFormatSizeKey="settings:favicon_image_format_size"
                          validImageFormatKey="settings:favicon_valid_image_format"
                          t={t}
                        />
                      </div>
                      <div className="flex justify-start items-center">
                        <div className="flex justify-start items-center bg-gray-100 border-r-indigo-200 border-dashed border-2 rounded-xl w-[9rem] h-[9rem] sm:w-[14rem] sm:h-[14rem] xl:w-[9rem] xl:h-[9rem]">
                          {orgDetails.organisation_favicon ? (
                            <img
                              src={orgDetails.organisation_favicon}
                              alt={t("settings:organisation_favicon")}
                              className="w-full h-full object-contain p-2 rounded-lg"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center p-3">
                              <FileImage
                                className="w-full h-full text-gray-400"
                                strokeWidth={1.75}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="w-full rounded-xl ">
                    <label
                      className="block text-sm font-medium text-gray-900"
                      htmlFor="org_logo"
                    >
                      {t("settings:organisation_logo")}
                    </label>
                    <div className="gap-5 flex flex-col 2xl:flex-row">
                      <div className="items-center flex-initial w-full mt-[0rem] 2xl:mt-[1rem]">
                        <LogoUpload
                          id="org-logo-upload"
                          logoHeading={t("settings:upload_your")}
                          logoSubHeading={t("settings:organisation_logo_label")}
                          altText={t("settings:upload_qr_code_image")}
                          onFileUpload={handleLogoUpload}
                          t={t}
                        />
                      </div>
                      <div className="flex justify-start items-center">
                        <div className="flex justify-start items-center bg-gray-100 border-r-indigo-200 border-dashed border-2 rounded-xl w-[9rem] h-[9rem] sm:w-[14rem] sm:h-[14rem] xl:w-[9rem] xl:h-[9rem]">
                          <img
                            src={
                              orgDetails.org_logo
                                ? orgDetails.org_logo
                                : PeviewImage
                            }
                            alt=""
                            className="w-full h-full object-contain p-2 rounded-lg"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col mt-3 xl:mt-0 mb-3">
                    <div className="text-lg font-medium text-gray-900">
                      {t("settings:urls")}
                    </div>
                    <div className="text-xs font-light text-[#3A3A3A]">
                      {t("settings:add_social_media_links")}
                    </div>
                  </div>
                  <div className="mb-4">
                    <label
                      className="block mb-2 text-sm font-medium text-gray-900"
                      htmlFor="org_linkedin_url"
                    >
                      {t("settings:linkedin")}
                    </label>
                    <input
                      type="text"
                      id="org_linkedin_url"
                      value={orgDetails.org_linkedin_url}
                      onChange={handleInputChange}
                      className={`bg-gray-50 border text-gray-600 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 ${fieldErrors.org_linkedin_url ? "border-red-500" : "border-gray-300"}`}
                      placeholder="e.g. https://linkedin.com/company/credissuer"
                    />
                    {fieldErrors.org_linkedin_url && (
                      <p className="text-red-500 text-sm mt-1">
                        {fieldErrors.org_linkedin_url}
                      </p>
                    )}
                  </div>
                  <div className="mb-4">
                    <label
                      className="block mb-2 text-sm font-medium text-gray-900"
                      htmlFor="org_twitter_url"
                    >
                      {t("settings:twitter")}
                    </label>
                    <input
                      type="text"
                      id="org_twitter_url"
                      value={orgDetails.org_twitter_url}
                      onChange={handleInputChange}
                      className={`bg-gray-50 border text-gray-600 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 ${fieldErrors.org_twitter_url ? "border-red-500" : "border-gray-300"}`}
                      placeholder="e.g. https://twitter.com/credissuer"
                    />
                    {fieldErrors.org_twitter_url && (
                      <p className="text-red-500 text-sm mt-1">
                        {fieldErrors.org_twitter_url}
                      </p>
                    )}
                  </div>
                  <div className="mb-4">
                    <label
                      className="block mb-2 text-sm font-medium text-gray-900"
                      htmlFor="org_instagram_url"
                    >
                      {t("settings:instagram")}
                    </label>
                    <input
                      type="text"
                      id="org_instagram_url"
                      value={orgDetails.org_instagram_url}
                      onChange={handleInputChange}
                      className={`bg-gray-50 border text-gray-600 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 ${fieldErrors.org_instagram_url ? "border-red-500" : "border-gray-300"}`}
                      placeholder="e.g. https://instagram.com/credissuer"
                    />
                    {fieldErrors.org_instagram_url && (
                      <p className="text-red-500 text-sm mt-1">
                        {fieldErrors.org_instagram_url}
                      </p>
                    )}
                  </div>
                  <div className="mb-4">
                    <label
                      className="block mb-2 text-sm font-medium text-gray-900"
                      htmlFor="org_facebook_url"
                    >
                      {t("settings:facebook")}
                    </label>
                    <input
                      type="text"
                      id="org_facebook_url"
                      value={orgDetails.org_facebook_url}
                      onChange={handleInputChange}
                      className={`bg-gray-50 border text-gray-600 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 ${fieldErrors.org_facebook_url ? "border-red-500" : "border-gray-300"}`}
                      placeholder="e.g. https://facebook.com/credissuer"
                    />
                    {fieldErrors.org_facebook_url && (
                      <p className="text-red-500 text-sm mt-1">
                        {fieldErrors.org_facebook_url}
                      </p>
                    )}
                  </div>
                  <div className="mb-4">
                    <label
                      className="block mb-2 text-sm font-medium text-gray-900"
                      htmlFor="org_youtube_url"
                    >
                      {t("settings:youtube")}
                    </label>
                    <input
                      type="text"
                      id="org_youtube_url"
                      value={orgDetails.org_youtube_url}
                      onChange={handleInputChange}
                      className={`bg-gray-50 border text-gray-600 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 ${fieldErrors.org_youtube_url ? "border-red-500" : "border-gray-300"}`}
                      placeholder="e.g. https://youtube.com/credissuer"
                    />
                    {fieldErrors.org_youtube_url && (
                      <p className="text-red-500 text-sm mt-1">
                        {fieldErrors.org_youtube_url}
                      </p>
                    )}
                  </div>
                  <div className="mb-4">
                    <label
                      className="block mb-2 text-sm font-medium text-gray-900"
                      htmlFor="org_whatsapp_url"
                    >
                      {t("settings:whatsapp")}
                    </label>
                    <input
                      type="text"
                      id="org_whatsapp_url"
                      value={orgDetails.org_whatsapp_url}
                      onChange={handleInputChange}
                      className={`bg-gray-50 border text-gray-600 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 ${fieldErrors.org_whatsapp_url ? "border-red-500" : "border-gray-300"}`}
                      placeholder="e.g. https://whatsapp.com/credissuer"
                    />
                    {fieldErrors.org_whatsapp_url && (
                      <p className="text-red-500 text-sm mt-1">
                        {fieldErrors.org_whatsapp_url}
                      </p>
                    )}
                  </div>
                </div>
                <div className="w-full xl:w-[50%] rounded-xl">
                  <div className="w-full flex justify-center items-center">
                    <div className="flex justify-center items-center mt-[0rem] xl:mt-[4.7rem] w-[9rem] h-[9rem] sm:w-[14rem] sm:h-[14rem] xl:w-[14rem] xl:h-[14rem]">
                      <img
                        src={
                          orgDetails.org_did_image
                            ? orgDetails.org_did_image
                            : qrCodeIcon
                        }
                        alt=""
                        className={`w-fit h-fit object-cover ${
                          orgDetails.org_did_image
                            ? "px-2 py-2"
                            : "animate-pulse mb-2"
                        }`}
                      />
                    </div>
                  </div>
                  <div className="sub-container1 mt-[2.6rem] px-[0.5rem] pb-[0.5rem] sm:px-[1.1rem] sm:pb-[1.1rem]">
                    <div className="flex flex-col sm:flex-row min-h-[5.5rem] sm:min-h-[7rem] items-center justify-between w-full bg-cfIssuedCredentialTemplateCardDdBG px-3 py-2.5 rounded-[0.25rem]">
                      <div className="flex-1 w-full">
                        <p className="text-xs sm:text-cfIssuedCredentialTemplateCardDdText w-full break-all leading-relaxed">
                          {orgDetails.org_did_key ? (
                            orgDetails.org_did_key
                          ) : (
                            <div className="animate-pulse flex flex-col w-full space-y-6 py-1">
                              <div className="space-y-3">
                                <div className="h-3 bg-[#ACACAC] rounded-xl w-full"></div>
                                <div className="grid grid-cols-3 gap-4">
                                  <div className="h-3 bg-[#ACACAC] rounded-xl col-span-2"></div>
                                  <div className="h-3 bg-[#ACACAC] rounded-xl col-span-1"></div>
                                </div>
                              </div>
                            </div>
                          )}
                        </p>
                      </div>
                      <div className="relative ">
                        {copied ? (
                          <img
                            src={correct}
                            alt="copied successfully"
                            className="h-7 w-7 sm:h-8 sm:w-8"
                          />
                        ) : (
                          orgDetails.org_did_key && (
                            <button
                              type="button"
                              className="focus:outline-none"
                              aria-label={t("settings:copy_did_key", {
                                defaultValue: "Copy DID Key",
                              })}
                              onClick={(e) => {
                                handleCopyClick(e, orgDetails.org_did_key);
                                Toast({
                                  type: "success",
                                  title: t("settings:success"),
                                  message: t("settings:did_key_copied"),
                                });
                              }}
                            >
                              <CopyIcon
                                size={32}
                                className="inline-flex h-8 w-8 sm:h-9 sm:w-9"
                                aria-hidden
                              />
                            </button>
                          )
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mb-4 ml-3">
                <BtnSave
                  Display_Icon={CgAttachment}
                  TriggerBtn={handleSave}
                  DisplayText={t("settings:save")}
                  type="submit"
                  formData={isFormDataChanged}
                />
              </div>
            </div>
          )}
          {selectedMenu === "Public Page Settings" && (
            <div className="flex flex-col">
              <div className="flex flex-col xl:flex-row w-full">
                <div className="p-3 xl:w-[50%]">
                  <div className="xs:pl-8 xs:pr-3 rounded-xl">
                    <header className="text-xl font-semibold mb-3 text-gray-900">
                      {t("settings:public_page_settings")}
                    </header>

                    {authContext.isWhiteLabelEnabled && (
                      <CustomDomainSection onDomainChange={setCustomDomain} />
                    )}

                    <div className="mb-6">
                      <div className="text-sm font-medium text-gray-900 mb-2">
                        {t("settings:public_page_branding")}
                      </div>

                      <div className="rounded-xl border border-gray-200 bg-white p-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label className="block mb-2 text-sm font-medium text-gray-900">
                              {t("settings:primary_colour")}
                            </label>
                            <div className="flex items-center gap-3">
                              <input
                                aria-label={t("settings:primary_colour")}
                                type="color"
                                value={getColorForPicker(
                                  orgDetails.org_domain_colours?.primary_colour,
                                  getDefaultBrandColours().primary_colour,
                                )}
                                onChange={(e) =>
                                  handleColourChange(
                                    "primary_colour",
                                    e.target.value,
                                  )
                                }
                                className="h-10 w-12 rounded-md border border-gray-300 bg-white p-1 cursor-pointer"
                              />
                              <input
                                type="text"
                                value={
                                  orgDetails.org_domain_colours
                                    ?.primary_colour || ""
                                }
                                onChange={(e) =>
                                  handleColourChange(
                                    "primary_colour",
                                    e.target.value,
                                  )
                                }
                                className={`bg-gray-50 border text-gray-600 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 ${
                                  fieldErrors.primary_colour
                                    ? "border-red-500"
                                    : "border-gray-300"
                                }`}
                                placeholder="#3538cd"
                                inputMode="text"
                              />
                            </div>
                            {fieldErrors.primary_colour && (
                              <p className="text-red-500 text-sm mt-1">
                                {fieldErrors.primary_colour}
                              </p>
                            )}
                          </div>

                          <div>
                            <label className="block mb-2 text-sm font-medium text-gray-900">
                              {t("settings:secondary_colour")}
                            </label>
                            <div className="flex items-center gap-3">
                              <input
                                aria-label={t("settings:secondary_colour")}
                                type="color"
                                value={getColorForPicker(
                                  orgDetails.org_domain_colours
                                    ?.secondary_colour,
                                  getDefaultBrandColours().secondary_colour,
                                )}
                                onChange={(e) =>
                                  handleColourChange(
                                    "secondary_colour",
                                    e.target.value,
                                  )
                                }
                                className="h-10 w-12 rounded-md border border-gray-300 bg-white p-1 cursor-pointer"
                              />
                              <input
                                type="text"
                                value={
                                  orgDetails.org_domain_colours
                                    ?.secondary_colour || ""
                                }
                                onChange={(e) =>
                                  handleColourChange(
                                    "secondary_colour",
                                    e.target.value,
                                  )
                                }
                                className={`bg-gray-50 border text-gray-600 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 ${
                                  fieldErrors.secondary_colour
                                    ? "border-red-500"
                                    : "border-gray-300"
                                }`}
                                placeholder="#45489e"
                                inputMode="text"
                              />
                            </div>
                            {fieldErrors.secondary_colour && (
                              <p className="text-red-500 text-sm mt-1">
                                {fieldErrors.secondary_colour}
                              </p>
                            )}
                          </div>
                        </div>

                        <BrandingPreviewCard
                          primaryColour={effectiveBrandColours.primary_colour}
                          secondaryColour={
                            effectiveBrandColours.secondary_colour
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="p-3 xl:w-[50%]">
                  <div
                    className="xs:pl-4 xs:pr-3"
                    id="settings-public-page-preview-section"
                    role="region"
                    aria-label={t("settings:preview_embed_aria_label")}
                  >
                    <div className="mb-3">
                      <header className="text-xl font-semibold text-gray-900">
                        {t("settings:public_page_preview")}
                      </header>
                      <p className="text-xs text-gray-500 mt-1">
                        {t("settings:preview_only")}
                      </p>
                    </div>
                    <div className="rounded-3xl border border-gray-200 ring-1 ring-black/5 bg-white overflow-hidden shadow-xl">
                      <div className="flex items-center justify-between px-3.5 py-2 bg-gray-50/90 backdrop-blur border-b border-gray-200">
                        <div className="flex items-center gap-3">
                          <div className="flex items-center gap-1.5">
                            <span className="h-2.5 w-2.5 rounded-full bg-red-400" />
                            <span className="h-2.5 w-2.5 rounded-full bg-amber-400" />
                            <span className="h-2.5 w-2.5 rounded-full bg-green-400" />
                          </div>
                          <span className="hidden sm:inline-block text-[0.65rem] uppercase tracking-wider font-semibold text-gray-500 bg-gray-200/70 rounded-full px-2 py-0.5">
                            {t("settings:preview_only")}
                          </span>
                        </div>

                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            id="settings-public-page-zoom-out"
                            aria-label={t("settings:preview_zoom_out")}
                            disabled={previewZoom <= 0.5}
                            onClick={() =>
                              setPreviewZoom(
                                (z) =>
                                  Math.round(Math.max(0.5, z - 0.1) * 10) / 10,
                              )
                            }
                            className="inline-flex items-center gap-1 px-2 py-1.5 rounded-lg text-gray-500 text-xs font-medium transition hover:bg-gray-200/70 disabled:opacity-30 disabled:cursor-not-allowed"
                            title={t("settings:preview_zoom_out")}
                          >
                            <ZoomOut className="h-3.5 w-3.5" />
                            <span className="hidden sm:inline">
                              {t("settings:preview_zoom_out")}
                            </span>
                          </button>

                          <span
                            id="settings-public-page-zoom-level"
                            className="min-w-[3rem] text-center text-xs font-medium text-gray-500 select-none tabular-nums"
                          >
                            {Math.round(previewZoom * 100)}%
                          </span>

                          <button
                            type="button"
                            id="settings-public-page-zoom-in"
                            aria-label={t("settings:preview_zoom_in")}
                            title={t("settings:preview_zoom_in")}
                            disabled={previewZoom >= 1.5}
                            onClick={() =>
                              setPreviewZoom(
                                (z) =>
                                  Math.round(Math.min(1.5, z + 0.1) * 10) / 10,
                              )
                            }
                            className="inline-flex items-center gap-1 px-2 py-1.5 rounded-lg text-gray-500 text-xs font-medium transition hover:bg-gray-200/70 disabled:opacity-30 disabled:cursor-not-allowed"
                          >
                            <ZoomIn className="h-3.5 w-3.5" />
                            <span className="hidden sm:inline">
                              {t("settings:preview_zoom_in")}
                            </span>
                          </button>

                          <span className="mx-1 h-4 w-px bg-gray-300" />

                          <a
                            id="settings-public-page-full-view"
                            href={orgDetails.preview_public_url || undefined}
                            target="_blank"
                            rel="noreferrer noopener"
                            aria-label={t("settings:preview_open_new_tab")}
                            title={t("settings:preview_open_new_tab")}
                            aria-disabled={!orgDetails.preview_public_url}
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition ${
                              orgDetails.preview_public_url
                                ? "text-cfPrimaryButton bg-cfPrimaryButton/10 hover:bg-cfPrimaryButton/20"
                                : "text-gray-300 pointer-events-none"
                            }`}
                          >
                            <ExternalLinkIcon
                              size={14}
                              className="inline-flex"
                              aria-hidden
                            />
                            <span className="hidden sm:inline">
                              {t("settings:preview_open_new_tab")}
                            </span>
                          </a>
                        </div>
                      </div>

                      {(() => {
                        let defaultHost = "";
                        try {
                          defaultHost = orgDetails.preview_public_url
                            ? new URL(orgDetails.preview_public_url).host
                            : "";
                        } catch {
                          defaultHost = "";
                        }
                        const addressBarHost = customDomain || defaultHost;
                        if (!addressBarHost) return null;
                        return (
                          <div className="px-3.5 py-2 border-b border-gray-200 bg-gray-50/60">
                            <div className="flex items-center gap-1.5 rounded-lg bg-white border border-gray-200 px-3 py-1.5 text-xs text-gray-500">
                              <Lock className="h-3 w-3 shrink-0 text-gray-400" />
                              <span className="truncate font-mono">
                                {addressBarHost}
                              </span>
                            </div>
                          </div>
                        );
                      })()}

                      {orgDetails.preview_public_url ? (
                        <div
                          className="relative w-full overflow-hidden bg-gray-100 group"
                          style={{ height: "70vh", minHeight: "420px" }}
                        >
                          <div className="w-full h-full overflow-y-auto overflow-x-hidden">
                            <div
                              style={{
                                width: `${100 / previewZoom}%`,
                                height: `${300 / previewZoom}%`,
                                transform: `scale(${previewZoom})`,
                                transformOrigin: "top left",
                              }}
                            >
                              <iframe
                                key={previewIframeKey}
                                src={orgDetails.preview_public_url}
                                title={t("settings:preview_embed_aria_label")}
                                className="w-full h-full border-0 pointer-events-none"
                                sandbox="allow-scripts allow-same-origin"
                                loading="lazy"
                              />
                            </div>
                          </div>
                          <div
                            className="absolute inset-0 pointer-events-none"
                            aria-hidden="true"
                          >
                            <div className="pointer-events-none absolute bottom-4 left-1/2 -translate-x-1/2 z-20 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                              <div className="flex items-center gap-2 bg-gray-900/85 text-white text-xs font-medium px-3.5 py-2 rounded-xl shadow-lg backdrop-blur-sm whitespace-nowrap">
                                <img
                                  src={WarningAmberIcon}
                                  className="h-3.5 w-3.5 shrink-0"
                                  alt=""
                                  aria-hidden="true"
                                />
                                {t("settings:preview_interactive_disabled")}
                              </div>
                              <div className="w-2.5 h-2.5 mx-auto mt-1 bg-gray-900/85 rotate-45 rounded-sm" />
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center justify-center py-20 px-4 text-center bg-gray-50/50">
                          <img src={GlobeIcon} height={60} width={60} alt="" />
                          <p className="text-sm text-gray-400">
                            {t("settings:preview_url_unavailable")}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="mb-4 ml-3">
                <BtnSave
                  Display_Icon={CgAttachment}
                  TriggerBtn={handleSave}
                  DisplayText={t("settings:save")}
                  type="submit"
                  formData={isFormDataChanged}
                />
              </div>
            </div>
          )}
          {selectedMenu === "Organisation Details" && (
            <div className="w-full 2xl:w-[50%] rounded-xl p-3">
              <div className="text-xl font-semibold text-gray-900">
                <p>{t("settings:linkedin_organisation_details")}</p>
              </div>
              <div className="flex flex-col w-full h-full justify-between">
                <div className="flex w-full justify-center items-center my-[2rem]">
                  <img src={LinkedInLiveIcon} height={150} width={150} alt="" />
                </div>
                <div>
                  <div className="mb-4">
                    <div className="flex">
                      <label
                        className="flex mb-2 text-sm font-medium text-gray-900"
                        htmlFor="org_domain"
                      >
                        {t("settings:organisation_id")}
                      </label>
                    </div>
                    <input
                      type="text"
                      id="org_linkedin_code"
                      value={orgDetails.org_linkedin_code}
                      onChange={handleInputChange}
                      className="bg-gray-50 border border-gray-300 text-gray-600 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:focus:ring-blue-500 dark:focus:border-blue-500"
                      placeholder="e.g. 1337"
                    />
                  </div>
                  <div className="mb-4">
                    <div className="flex">
                      <label
                        className="flex mb-2 text-sm font-medium text-gray-900"
                        htmlFor="org_domain"
                      >
                        {t("settings:organisation_name")}
                      </label>
                    </div>
                    <input
                      type="text"
                      id="org_linkedin_name"
                      value={orgDetails.org_linkedin_name}
                      onChange={handleInputChange}
                      className="bg-gray-50 border border-gray-300 text-gray-600 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:focus:ring-blue-500 dark:focus:border-blue-500"
                      placeholder="e.g. Credissuer"
                    />
                  </div>
                  <div className="mb-4">
                    <BtnSave
                      Display_Icon={CgAttachment}
                      TriggerBtn={handleSave}
                      DisplayText={t("settings:save")}
                      type="submit"
                      formData={isFormDataChanged}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
          {selectedMenu === "Notification Settings" && (
            <aside className="flex flex-col gap-1 p-3 w-full lg:w-1/2 xl:w-[50%] 2xl:w-[35%]">
              <section className="flex flex-col bg-white lg:w-full md:grow xs:pt-8 xs:pb-8 lg:pb-2 rounded-xl">
                <header className="text-xl font-semibold leading-8 mb-4 text-gray-900">
                  {t("settings:notification_settings")}
                </header>
                <SettingOption
                  key={"toggle_email"}
                  toggleStatus={orgDetails.enable_email_notification}
                  heading={t("settings:email")}
                  subheading={t("settings:enable_email")}
                  onclickFunction={() =>
                    handleToggleChange("enable_email_notification")
                  }
                  label={t("settings:email")}
                  description={t("settings:enable_email")}
                />
              </section>
              <div className="mb-4">
                <BtnSave
                  Display_Icon={CgAttachment}
                  TriggerBtn={handleSave}
                  DisplayText={t("settings:save")}
                  type="submit"
                  formData={isFormDataChanged}
                />
              </div>
            </aside>
          )}
          {selectedMenu === "Customize QR Code" && (
            <div className="rounded-xl p-3">
              <div className="text-xl font-semibold text-gray-900">
                <p>{t("settings:customized_qr_code")}</p>
              </div>
              <div className="flex flex-col lg:flex-row w-full justify-start gap-5 mt-[1rem]">
                <div className="w-full lg:w-[40%] flex-wrap">
                  <div className="items-center flex-initial w-full mt-[0rem] xl:mt-[4.9rem]">
                    <LogoUpload
                      id="qr-code-upload"
                      logoHeading={t("settings:click_to_upload")}
                      logoSubHeading={t("settings:drag_and_drop")}
                      altText="Upload QR code image"
                      onFileUpload={handleQrCodeUpload}
                      t={t}
                    />
                  </div>
                </div>
                <div
                  id="qr-code"
                  className="flex justify-center items-center lg:border lg:border-neutral-200 rounded-xl p-[0.5rem]"
                ></div>
              </div>

              <div className="mt-3">
                <BtnSave
                  Display_Icon={CgAttachment}
                  TriggerBtn={handleSave}
                  DisplayText={t("settings:save")}
                  type="submit"
                  formData={isFormDataChanged}
                />
              </div>
            </div>
          )}

          {selectedMenu === "Verification Settings" && (
            <aside className="flex flex-col gap-1 p-3 w-full lg:w-4/5 xl:w-[60%] 2xl:w-[45%]">
              <section className="flex flex-col bg-white lg:w-full md:grow xs:pt-8 xs:pb-8 lg:pb-2 rounded-xl">
                <header className="text-xl font-semibold leading-8 mb-4 text-gray-900">
                  {t("settings:verification_settings")}
                </header>
                <h2 className="text-lg font-semibold leading-6 mb-3 text-gray-700">
                  {t("settings:manage_vc")}
                </h2>
                <SettingOption
                  key={"toggle_is_vc_admin_approval_required"}
                  toggleStatus={
                    verificationSettings.is_vc_admin_approval_required
                  }
                  heading={t("settings:enable_admin_approval_vc")}
                  onclickFunction={() =>
                    handleVerificationToggleChange(
                      "is_vc_admin_approval_required",
                    )
                  }
                  label={t("settings:enable_admin_approval_vc")}
                />

                <SettingOption
                  key={"toggle_is_payment_required_for_vc"}
                  toggleStatus={verificationSettings.is_payment_required_for_vc}
                  heading={t("settings:enable_paid_verification_vc")}
                  onclickFunction={() =>
                    handleVerificationToggleChange("is_payment_required_for_vc")
                  }
                  label={t("settings:enable_paid_verification_vc")}
                />
                {verificationSettings.is_payment_required_for_vc && (
                  <>
                    <h2 className="text-lg font-semibold leading-6 mt-3 mb-3 text-gray-700">
                      {t("settings:paid_verification_charges_rupees")}
                    </h2>
                    <div className="mb-4">
                      <label
                        htmlFor="verification_cost_vc"
                        className="block mb-1 text-sm font-medium mb-2 text-gray-500"
                      >
                        {t("settings:verifier_pay_amount_vc")}
                      </label>
                      <input
                        type="text"
                        id="verification_cost_vc"
                        name="verification_cost_vc"
                        value={verificationSettings.verification_cost_vc || ""}
                        onChange={handleVerificationCostChange}
                        className="bg-gray-50 border border-gray-300 text-gray-600 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                        placeholder={t("settings:enter_amount")}
                      />
                      {errors.verification_cost_vc && (
                        <p className="text-red-500 text-sm mt-1">
                          {errors.verification_cost_vc}
                        </p>
                      )}
                    </div>
                  </>
                )}
                <h2 className="text-lg font-semibold leading-6 mb-3 mt-4 text-gray-700">
                  {t("settings:manage_legacy_certificates")}
                </h2>
                <SettingOption
                  key={"toggle_is_legacy_certificate_enabled"}
                  toggleStatus={
                    verificationSettings.is_legacy_certificate_enabled
                  }
                  heading={t(
                    "settings:enable_verification_legacy_certificates",
                  )}
                  onclickFunction={() =>
                    handleVerificationToggleChange(
                      "is_legacy_certificate_enabled",
                    )
                  }
                  label={t("settings:enable_verification_legacy_certificates")}
                />
                <SettingOption
                  key={"toggle_is_legacy_admin_approval_required"}
                  toggleStatus={
                    verificationSettings.is_legacy_admin_approval_required
                  }
                  heading={t("settings:enable_admin_approval_legacy")}
                  onclickFunction={() =>
                    handleVerificationToggleChange(
                      "is_legacy_admin_approval_required",
                    )
                  }
                  label={t("settings:enable_admin_approval_legacy")}
                />

                <SettingOption
                  key={"toggle_is_payment_required_for_legacy"}
                  toggleStatus={
                    verificationSettings.is_payment_required_for_legacy
                  }
                  heading={t("settings:enable_paid_verification_legacy")}
                  onclickFunction={() =>
                    handleVerificationToggleChange(
                      "is_payment_required_for_legacy",
                    )
                  }
                  label={t("settings:enable_paid_verification_legacy")}
                />

                {verificationSettings.is_payment_required_for_legacy && (
                  <>
                    <h2 className="text-lg font-semibold leading-6 mt-3 mb-3 text-gray-700">
                      {t("settings:paid_verification_charges_rupees_legacy")}
                    </h2>
                    <div className="mb-4">
                      <label
                        htmlFor="verification_cost"
                        className="block mb-1 text-sm font-medium mb-2 text-gray-500"
                      >
                        {t("settings:verifier_pay_amount_legacy")}
                      </label>
                      <input
                        type="text"
                        id="verification_cost"
                        name="verification_cost"
                        value={verificationSettings.verification_cost || ""}
                        onChange={handleVerificationCostChange}
                        className="bg-gray-50 border border-gray-300 text-gray-600 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                        placeholder={t("settings:enter_amount")}
                      />
                      {errors.verification_cost && (
                        <p className="text-red-500 text-sm mt-1">
                          {errors.verification_cost}
                        </p>
                      )}
                    </div>
                  </>
                )}
              </section>
              <div className="mb-4">
                <BtnSave
                  Display_Icon={CgAttachment}
                  TriggerBtn={handleVerificationSave}
                  DisplayText={t("settings:save")}
                  type="submit"
                  formData={isFormDataChanged}
                />
              </div>
            </aside>
          )}
        </div>
      </div>
    </div>
  );
}

export default SettingTemplate;
